import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  DirectionsRenderer,
  InfoWindow,
} from '@react-google-maps/api';
import { useMapLink } from './MapLinkContext';

interface LatLng {
  lat: number;
  lng: number;
}

interface MarkerData {
  id: string;
  lat?: number;
  lng?: number;
  address?: string;
  label?: string;
  color?: string;
}

const MARKER_COLORS: Record<string, string> = {
  red: 'FF0000',
  blue: '4285F4',
  green: '34A853',
  yellow: 'FBBC04',
  orange: 'FF6D01',
  purple: '9334E6',
  pink: 'E91E63',
};

interface LocationInput {
  lat?: number;
  lng?: number;
  address?: string;
}

interface DirectionsData {
  origin: LocationInput;
  destination: LocationInput;
  waypoints?: LocationInput[];
  travelMode?: string;
}

interface MapData {
  center?: LocationInput;
  zoom?: number;
  markers?: MarkerData[];
  directions?: DirectionsData;
}

interface ProjectAppMapBlockProps {
  data: Record<string, unknown>;
}

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

const containerStyle: React.CSSProperties = {
  width: '100%',
  height: 'clamp(320px, 80svh, 600px)',
  borderRadius: '8px',
};

const defaultCenter = { lat: 51.5074, lng: -0.1278 };

/**
 * Resolve a LocationInput to a google.maps.LatLng.
 * Accepts { lat, lng } or { address }.
 */
function resolveLocation(loc: LocationInput): google.maps.LatLng | string {
  if (loc.lat != null && loc.lng != null) {
    return new google.maps.LatLng(loc.lat, loc.lng);
  }
  if (loc.address) {
    return loc.address;
  }
  return new google.maps.LatLng(0, 0);
}

const ProjectAppMapBlock: React.FC<ProjectAppMapBlockProps> = ({ data }) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className='project-app-block-map--no-key'>
        Google Maps API key not configured. Set REACT_APP_GOOGLE_MAPS_API_KEY.
      </div>
    );
  }

  if (loadError) {
    return (
      <div className='project-app-block-map--no-key'>
        Failed to load Google Maps.
      </div>
    );
  }

  if (!isLoaded) {
    return <div className='project-app-block-map--no-key'>Loading map…</div>;
  }

  return <ProjectAppMapBlockInner data={data} />;
};

const ProjectAppMapBlockInner: React.FC<ProjectAppMapBlockProps> = ({
  data,
}) => {
  const { hoveredLinkId, setHoveredLinkId } = useMapLink();
  const { center, zoom, markers, directions } = data as MapData;

  const [resolvedPositions, setResolvedPositions] = useState<
    Map<string, LatLng>
  >(new Map());
  const [resolvedCenter, setResolvedCenter] = useState<LatLng | null>(null);
  const [directionsResult, setDirectionsResult] =
    useState<google.maps.DirectionsResult | null>(null);
  const [activeInfoWindow, setActiveInfoWindow] = useState<string | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const directionsRequested = useRef(false);
  const geocodeRequested = useRef(false);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRefs = useRef<Map<string, google.maps.Marker>>(new Map());
  const localHoverRef = useRef<string | null>(null);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setMapLoaded(true);
  }, []);

  // Geocode markers that have address instead of lat/lng
  useEffect(() => {
    if (geocodeRequested.current || !markers) return;
    const needsGeocode = markers.filter(
      (m) => m.address && (m.lat == null || m.lng == null),
    );
    if (needsGeocode.length === 0) return;
    geocodeRequested.current = true;

    const geocoder = new google.maps.Geocoder();
    const resolved = new Map<string, LatLng>();

    Promise.all(
      needsGeocode.map(
        (m) =>
          new Promise<void>((resolve) => {
            geocoder.geocode({ address: m.address! }, (results, status) => {
              if (
                status === google.maps.GeocoderStatus.OK &&
                results &&
                results[0]
              ) {
                const loc = results[0].geometry.location;
                resolved.set(m.id, { lat: loc.lat(), lng: loc.lng() });
              } else {
                console.warn(
                  '[MapBlock] Geocode failed for:',
                  m.address,
                  status,
                );
              }
              resolve();
            });
          }),
      ),
    ).then(() => {
      setResolvedPositions((prev) => {
        const next = new Map(prev);
        resolved.forEach((v, k) => next.set(k, v));
        return next;
      });
    });
  }, [markers]);

  // Geocode center if it's an address
  useEffect(() => {
    if (!center || resolvedCenter || (center.lat != null && center.lng != null))
      return;
    if (!center.address) return;

    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: center.address }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
        const loc = results[0].geometry.location;
        setResolvedCenter({ lat: loc.lat(), lng: loc.lng() });
      }
    });
  }, [center, resolvedCenter]);

  // Imperatively bounce markers only when hover comes from TABLE (not from map)
  useEffect(() => {
    markerRefs.current.forEach((gMarker, id) => {
      if (id === hoveredLinkId && id !== localHoverRef.current) {
        gMarker.setAnimation(google.maps.Animation.BOUNCE);
      } else {
        gMarker.setAnimation(null);
      }
    });
  }, [hoveredLinkId]);

  // Request directions when map loads
  useEffect(() => {
    if (!directions || directionsRequested.current || !mapLoaded) return;
    directionsRequested.current = true;

    const directionsService = new google.maps.DirectionsService();
    const waypoints = (directions.waypoints || []).map((wp) => ({
      location: resolveLocation(wp),
      stopover: true,
    }));

    directionsService.route(
      {
        origin: resolveLocation(directions.origin),
        destination: resolveLocation(directions.destination),
        waypoints,
        travelMode:
          (directions.travelMode as google.maps.TravelMode) ||
          google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === google.maps.DirectionsStatus.OK && result) {
          setDirectionsResult(result);
        } else {
          console.warn('Directions request failed:', status);
        }
      },
    );
  }, [directions, mapLoaded]);

  // Compute initial center (used only for first render)
  const initialCenter = useMemo<LatLng>(() => {
    if (center?.lat != null && center?.lng != null) {
      return { lat: center.lat, lng: center.lng };
    }
    if (markers && markers.length > 0) {
      const first = markers[0];
      if (first.lat != null && first.lng != null) {
        return { lat: first.lat, lng: first.lng };
      }
    }
    return defaultCenter;
  }, []);
  const initialZoom = zoom || 12;

  // Pan to geocoded center when it resolves
  useEffect(() => {
    if (resolvedCenter && mapRef.current) {
      mapRef.current.panTo(resolvedCenter);
    }
  }, [resolvedCenter]);

  return (
    <div className='project-app-block-map'>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={initialCenter}
        zoom={initialZoom}
        onLoad={onMapLoad}
        options={{
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }],
            },
          ],
        }}
      >
        {markers?.map((marker) => {
          // Resolve position: explicit lat/lng or geocoded address
          let position: LatLng | null = null;
          if (marker.lat != null && marker.lng != null) {
            position = { lat: marker.lat, lng: marker.lng };
          } else {
            const resolved = resolvedPositions.get(marker.id);
            if (resolved) {
              position = resolved;
            }
          }
          if (!position) return null; // Not yet geocoded
          return (
            <Marker
              key={marker.id}
              position={position}
              icon={
                marker.color && MARKER_COLORS[marker.color]
                  ? `https://maps.google.com/mapfiles/ms/icons/${marker.color}-dot.png`
                  : undefined
              }
              onLoad={(gMarker) => {
                console.log(
                  '[MapBlock] Marker onLoad — storing ref for:',
                  marker.id,
                );
                markerRefs.current.set(marker.id, gMarker);
              }}
              onUnmount={() => {
                markerRefs.current.delete(marker.id);
              }}
              onMouseOver={() => {
                console.log('[MapBlock] onMouseOver marker:', marker.id);
                localHoverRef.current = marker.id;
                setHoveredLinkId(marker.id);
              }}
              onMouseOut={() => {
                console.log('[MapBlock] onMouseOut marker:', marker.id);
                localHoverRef.current = null;
                setHoveredLinkId(null);
              }}
              onClick={() =>
                setActiveInfoWindow(
                  activeInfoWindow === marker.id ? null : marker.id,
                )
              }
            >
              {activeInfoWindow === marker.id && marker.label && (
                <InfoWindow onCloseClick={() => setActiveInfoWindow(null)}>
                  <div style={{ fontWeight: 600, fontSize: '13px' }}>
                    {marker.label}
                  </div>
                </InfoWindow>
              )}
            </Marker>
          );
        })}

        {directionsResult && (
          <DirectionsRenderer
            directions={directionsResult}
            options={{
              suppressMarkers: (markers?.length ?? 0) > 0,
              polylineOptions: {
                strokeColor: '#4285F4',
                strokeWeight: 5,
                strokeOpacity: 0.8,
              },
            }}
          />
        )}
      </GoogleMap>
    </div>
  );
};

export default ProjectAppMapBlock;
